import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import type { AuditEventV1 } from '@ownyou/shared-types';
import { useAuth } from './AuthContext';
import { useStore } from './StoreContext';
import {
  AuditEventSink,
  IndexedDbAuditKeyStore,
  type AuditKeyStore,
  type AuditProducerEventDraft,
  subscribeAuditProducerEvents,
} from '../lib/audit';

interface AuditContextValue {
  recordAuditEvent: (draft: AuditProducerEventDraft) => Promise<AuditEventV1 | null>;
  isReady: boolean;
  deviceId: string;
  keyStore: AuditKeyStore;
}

const AuditContext = createContext<AuditContextValue | null>(null);

export function AuditProvider({ children }: { children: ReactNode }) {
  const { signer, wallet, isAuthenticated } = useAuth();
  const { store, isReady: storeReady } = useStore();
  const deviceId = useMemo(() => getOrCreateAuditDeviceId(), []);
  const keyStore = useMemo<AuditKeyStore>(() => new IndexedDbAuditKeyStore(), []);
  const sinkRef = useRef<AuditEventSink | null>(null);
  const isAuditReady = Boolean(store && signer && isAuthenticated && storeReady && wallet);

  useEffect(() => {
    sinkRef.current = isAuditReady && store && signer
      ? new AuditEventSink({ store, signer, keyStore, deviceId })
      : null;
  }, [store, signer, keyStore, deviceId, isAuditReady]);

  const value = useMemo<AuditContextValue>(() => ({
    isReady: isAuditReady,
    deviceId,
    keyStore,
    recordAuditEvent: async (draft) => {
      if (!sinkRef.current) return null;
      return sinkRef.current.record(draft);
    },
  }), [deviceId, isAuditReady, keyStore]);

  useEffect(() => subscribeAuditProducerEvents(async (draft) => {
    if (!sinkRef.current) return;
    await sinkRef.current.record(draft);
  }), []);

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
}

export function useAudit() {
  const context = useContext(AuditContext);
  if (!context) {
    throw new Error('useAudit must be used within an AuditProvider');
  }
  return context;
}

const AUDIT_DEVICE_ID_KEY = 'ownyou.audit.deviceId';

function getOrCreateAuditDeviceId(): string {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(AUDIT_DEVICE_ID_KEY);
    if (stored) return stored;
    const next = `audit-device:${randomHex(16)}`;
    localStorage.setItem(AUDIT_DEVICE_ID_KEY, next);
    return next;
  }
  return `audit-device:${randomHex(16)}`;
}

function randomHex(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}
